import { useRef } from "react";
import { Camera, X, MessageCircle, Phone, Mail } from "lucide-react";
import { Field, inputCls, StepError } from "./WizardBits";

const CONTACTS = [["WhatsApp", MessageCircle], ["Telefon", Phone], ["Email", Mail]];

const Photos = ({ d, w }) => {
  const ref = useRef(null);
  const add = (files) => w.setPhotos((p) => [...p, ...Array.from(files || [])].slice(0, 5));
  return (
    <div>
      <button type="button" data-testid="wizard-photos-add" onClick={() => ref.current?.click()}
        className="w-full flex items-center gap-3 px-4 py-3.5 border border-dashed border-white/20 hover:border-gold transition-colors text-left">
        <Camera size={17} className="text-gold" />
        <span className="flex-1 text-[14px] text-zinc-300">{d.photos}</span>
        <span className="text-[12px] text-zinc-500 font-mono">{w.photos.length}/5</span>
      </button>
      <input ref={ref} type="file" accept="image/*" multiple className="hidden" data-testid="wizard-photos-input" onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
      {w.photos.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2">
          {w.photos.map((f, i) => (
            <li key={`${f.name}-${i}`} className="inline-flex items-center gap-2 border border-white/10 pl-3 pr-1 py-1 text-[12px] text-zinc-300 max-w-[200px]">
              <span className="truncate">{f.name}</span>
              <button type="button" aria-label="remove" data-testid={`wizard-photo-remove-${i}`} onClick={() => w.setPhotos((p) => p.filter((_, j) => j !== i))} className="p-1 text-zinc-500 hover:text-gold"><X size={12} /></button>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-[12px] text-zinc-500 leading-relaxed">{d.photosHint}</p>
    </div>
  );
};

export const StepDetails = ({ c, w }) => {
  const d = c.details, e = w.errors, v = w.data;
  const isSofa = v.service === "sofa", isOther = v.service === "other";
  const itemLabel = isSofa ? d.furniture : isOther ? d.object : d.vehicle;
  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        <Field label={d.name} error={e.name} htmlFor="name">
          <input id="name" data-testid="wizard-input-name" autoComplete="name" value={v.name} onChange={(x) => w.set("name", x.target.value)} className={inputCls} />
        </Field>
        <Field label={d.phone} error={e.phone} htmlFor="phone">
          <input id="phone" data-testid="wizard-input-phone" type="tel" autoComplete="tel" inputMode="tel" value={v.phone} onChange={(x) => w.set("phone", x.target.value)} placeholder="+49 …" className={inputCls} />
        </Field>
        <Field label={d.email} hint={v.contact === "Email" ? null : d.optional} error={e.email} htmlFor="email">
          <input id="email" data-testid="wizard-input-email" type="email" autoComplete="email" value={v.email} onChange={(x) => w.set("email", x.target.value)} className={inputCls} />
        </Field>
        <Field label={itemLabel} hint={d.optional} htmlFor="item">
          <input id="item" data-testid="wizard-input-item" value={v.item} onChange={(x) => w.set("item", x.target.value)} placeholder={isSofa ? d.objectPlaceholderSofa : isOther ? "" : d.objectPlaceholderCar} className={inputCls} />
        </Field>
      </div>
      <Field label={d.notes} hint={d.optional} htmlFor="notes">
        <textarea id="notes" data-testid="wizard-input-notes" rows={3} value={v.notes} onChange={(x) => w.set("notes", x.target.value)} placeholder={d.notesPlaceholder} className={`${inputCls} resize-none`} />
      </Field>
      <Photos d={d} w={w} />
      <div>
        <div className="mb-3 text-[12px] tracking-[0.2em] uppercase text-zinc-300">{d.contact}</div>
        <div role="radiogroup" aria-label={d.contact} className="grid grid-cols-3 gap-2">
          {CONTACTS.map(([k, Icon]) => (
            <button key={k} type="button" role="radio" aria-checked={v.contact === k} data-testid={`wizard-contact-${k}`} onClick={() => w.set("contact", k)}
              className={`inline-flex items-center justify-center gap-2 px-3 py-3 border text-[14px] transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-gold ${v.contact === k ? "border-gold bg-gold/10 text-white" : "border-white/10 text-zinc-300 hover:border-white/30"}`}>
              <Icon size={15} className={v.contact === k ? "text-gold" : ""} /> {d.contacts[k]}
            </button>
          ))}
        </div>
      </div>
      <StepError text={e.photos} testid="wizard-error-photos" />
    </div>
  );
};
